import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Show } from '@components/Show';
import { useBlockEdit, useBlockEditFormStore } from '@ContractBuilder/modules/block-edit';
import { DrawerLayoutComponent } from '@ContractBuilder/modules/drawer-layout';
import { useDrawerStore } from '@ContractBuilder/store/drawer.store';
import { getElementId } from '@ContractBuilder/utils/get-element-id';
import { ArtificialCustomEvent, useCustomEventListener } from '@providers/events';
import { scrollIntoView } from '@root/src/utils/scroll-into-view';
import { AnimatePresence, motion } from 'motion/react';

import { blockCreatorClasses } from '../classes';
import { animationProps, BLOCK_CREATOR_STEPS } from '../constants';
import { BlockCreatorContext } from '../context/context';
import type { BlockCreatorStepId } from '../types';
import { AddBlockIcon } from '../views/AddBlockIcon';
import { BlockCreatorView } from '../views/BlockCreatorView';

interface BlockCreatorControllerProps {
  id: string;
  order: number;
  sectionId: string;
}

export const BlockCreatorController: FC<BlockCreatorControllerProps> = ({ id, order, sectionId }) => {
  'use memo';
  const [step, setStep] = useState<'closed' | BlockCreatorStepId>('closed');
  const isCreating = step !== 'closed';

  const { editingBlockId, setFormValues } = useBlockEditFormStore(({ formValues, setFormValues }) => ({
    editingBlockId: formValues?.id,
    setFormValues,
  }));

  const openDrawer = useDrawerStore(({ openDrawer }) => openDrawer);

  const hasEditMode = editingBlockId !== undefined;

  const { resetState: onCancel, setOnCancelCallback } = useBlockEdit();

  const handleAbort = useCallback(() => {
    setStep('closed');
  }, []);

  const handleCancel = useCallback(() => {
    onCancel();
    handleAbort();
  }, [onCancel, handleAbort]);

  useCustomEventListener(ArtificialCustomEvent.ExitBlockCreator, handleAbort);

  const goToStep = useCallback(
    (stepId: BlockCreatorStepId) => {
      if (stepId === 'wysiwyg') {
        setOnCancelCallback({ fn: handleAbort });

        openDrawer({
          props: { onCancel },
          shouldCloseOnClickAway: false,
          type: DrawerLayoutComponent.BlockConfigurationPanel,
        });
      }

      setStep(stepId);
    },
    [setOnCancelCallback, openDrawer, onCancel, handleAbort],
  );

  const onPrevStep = useCallback(
    () => setStep((current) => (current === 'closed' ? current : (BLOCK_CREATOR_STEPS[current].prevStepId ?? 'start'))),
    [],
  );

  const handleCreate = () => {
    setStep('start');
    setFormValues({ section_id: sectionId, order, id: '', type: 'mrc-heading', canEdit: true, canDelete: true });
    scrollIntoView(getElementId('block-creator', id), { block: 'center' }, 400);
  };

  const handlers = useMemo(
    () => ({
      goToStep,
      onPrevStep,
      handleCancel,
    }),
    [goToStep, onPrevStep, handleCancel],
  );

  return (
    <BlockCreatorContext.Provider value={handlers}>
      <AnimatePresence mode="wait">
        {step !== 'closed' && (
          <motion.div className={blockCreatorClasses(step)} {...animationProps}>
            <BlockCreatorView id={id} step={step} />
          </motion.div>
        )}
      </AnimatePresence>
      <Show when={!isCreating && !hasEditMode}>
        <AddBlockIcon onCreate={handleCreate} />
      </Show>
    </BlockCreatorContext.Provider>
  );
};
